import { gsap } from 'gsap';

export type TransitionMode = 'admin' | 'home' | 'login' | 'logout';

interface ColorTheme {
  primary: string;
  secondary: string;
}

const COLOR_THEMES: Record<TransitionMode, ColorTheme> = {
  admin: { primary: '#F4D35E', secondary: '#E8A317' },
  home: { primary: '#00D4FF', secondary: '#0088CC' },
  login: { primary: '#00FF88', secondary: '#00AA66' },
  logout: { primary: '#FF3366', secondary: '#1A0011' },
};

/**
 * Plays the full arcade grid cover → reveal transition.
 * Pure GSAP — no React dependency.
 */
export function playArcadeTransition(
  tiles: HTMLElement[],
  textEl: HTMLElement,
  scanlinesEl: HTMLElement,
  overlayEl: HTMLElement,
  mode: TransitionMode,
  onMidpoint: () => void,
  onComplete: () => void,
) {
  const { primary, secondary } = COLOR_THEMES[mode];
  const tl = gsap.timeline();

  // Reset tiles
  gsap.set(tiles, { scale: 0, opacity: 0, backgroundColor: primary });
  gsap.set(textEl, { scale: 0.5, opacity: 0 });
  gsap.set(scanlinesEl, { opacity: 0 });

  // ── COVER (0.6s) ──
  tl.set(overlayEl, { visibility: 'visible', pointerEvents: 'all' })
    .to(tiles, {
      scale: 1,
      opacity: 1,
      duration: 0.45,
      stagger: { each: 0.008, from: 'center', grid: [10, 10] },
      ease: 'power2.out',
    }, 0)
    .to(scanlinesEl, { opacity: 0.15, duration: 0.3 }, 0.1)
    .to(tiles, {
      backgroundColor: secondary,
      duration: 0.3,
      stagger: { each: 0.006, from: 'start', grid: [10, 10] },
    }, 0.25)
    .to(textEl, {
      scale: 1,
      opacity: 1,
      duration: 0.25,
      ease: 'back.out(1.7)',
    }, 0.35)
    .call(onMidpoint, undefined, 0.6);

  // ── HOLD (0.2s) — pulse back to primary ──
  tl.to(tiles, {
    backgroundColor: primary,
    duration: 0.2,
    stagger: { each: 0.004, from: 'center', grid: [10, 10] },
  }, 0.6);

  // ── REVEAL (0.6s) ──
  tl.to(textEl, {
    opacity: 0,
    scale: 1.2,
    duration: 0.2,
    ease: 'power1.in',
  }, 0.8)
    .to(tiles, {
      scale: 0,
      opacity: 0,
      duration: 0.45,
      stagger: { each: 0.008, from: 'edges', grid: [10, 10] },
      ease: 'power2.in',
    }, 0.85)
    .to(scanlinesEl, { opacity: 0, duration: 0.2 }, 0.9)
    .set(overlayEl, { visibility: 'hidden', pointerEvents: 'none' }, 1.4)
    .call(onComplete, undefined, 1.4);

  return tl;
}

/**
 * "Digital Materialization" login transition (~1.8s).
 * Diagonal wave cover → "WELCOME BACK" text → shatter reveal.
 */
export function playLoginTransition(
  tiles: HTMLElement[],
  textEl: HTMLElement,
  scanlinesEl: HTMLElement,
  overlayEl: HTMLElement,
  onMidpoint: () => void,
  onComplete: () => void,
) {
  const { primary, secondary } = COLOR_THEMES.login;
  const tl = gsap.timeline();

  // Reset tiles
  gsap.set(tiles, { scale: 0, opacity: 0, backgroundColor: primary, x: 0, y: 0, rotation: 0 });
  gsap.set(textEl, { scale: 0.5, opacity: 0 });
  gsap.set(scanlinesEl, { opacity: 0 });

  // ── COVER (0–0.6s) — diagonal wave sweep ──
  tl.set(overlayEl, { visibility: 'visible', pointerEvents: 'all' })
    .to(tiles, {
      scale: 1,
      opacity: 1,
      duration: 0.5,
      stagger: { each: 0.006, from: 'start', grid: [10, 10] },
      ease: 'power2.out',
    }, 0)
    .to(scanlinesEl, { opacity: 0.15, duration: 0.3 }, 0.1)
    .to(tiles, {
      backgroundColor: secondary,
      duration: 0.3,
      stagger: { each: 0.005, from: 'start', grid: [10, 10] },
    }, 0.25);

  // ── HOLD (0.6–0.9s) — text + midpoint callback ──
  tl.to(textEl, {
    scale: 1,
    opacity: 1,
    duration: 0.25,
    ease: 'back.out(1.7)',
  }, 0.55)
    .call(onMidpoint, undefined, 0.7)
    .to(tiles, {
      backgroundColor: primary,
      duration: 0.2,
      stagger: { each: 0.004, from: 'center', grid: [10, 10] },
    }, 0.7);

  // ── SHATTER REVEAL (0.9–1.8s) — tiles explode outward ──
  tl.to(textEl, {
    opacity: 0,
    scale: 1.3,
    duration: 0.2,
    ease: 'power1.in',
  }, 0.9)
    .to(tiles, {
      x: () => gsap.utils.random(-200, 200),
      y: () => gsap.utils.random(-200, 200),
      rotation: () => gsap.utils.random(-180, 180),
      scale: 0,
      opacity: 0,
      duration: 0.7,
      stagger: { each: 0.006, from: 'center', grid: [10, 10] },
      ease: 'power3.in',
    }, 0.95)
    .to(scanlinesEl, { opacity: 0, duration: 0.2 }, 1.0)
    .set(overlayEl, { visibility: 'hidden', pointerEvents: 'none' }, 1.8)
    // Reset tiles to clean state
    .set(tiles, { x: 0, y: 0, rotation: 0, scale: 0, opacity: 0 }, 1.8)
    .call(onComplete, undefined, 1.8);

  return tl;
}

/**
 * "CRT Shutdown" logout transition (~1.6s).
 * Row-by-row cascade → "GOODBYE" text → vertical compress + white flash.
 */
export function playLogoutTransition(
  tiles: HTMLElement[],
  textEl: HTMLElement,
  scanlinesEl: HTMLElement,
  overlayEl: HTMLElement,
  onMidpoint: () => void,
  onComplete: () => void,
) {
  const { primary, secondary } = COLOR_THEMES.logout;
  const tl = gsap.timeline();

  // Reset tiles
  gsap.set(tiles, { scale: 0, scaleX: 1, scaleY: 1, opacity: 0, backgroundColor: primary, x: 0, y: 0, rotation: 0 });
  gsap.set(textEl, { scale: 0.5, opacity: 0 });
  gsap.set(scanlinesEl, { opacity: 0 });

  // ── COVER (0–0.5s) — row-by-row cascade from top ──
  tl.set(overlayEl, { visibility: 'visible', pointerEvents: 'all' })
    .to(tiles, {
      scale: 1,
      opacity: 1,
      duration: 0.4,
      stagger: { each: 0.004, from: 'start', grid: [10, 10], axis: 'y' },
      ease: 'power2.out',
    }, 0)
    .to(scanlinesEl, { opacity: 0.15, duration: 0.3 }, 0.1)
    .to(tiles, {
      backgroundColor: secondary,
      duration: 0.3,
      stagger: { each: 0.003, from: 'start', grid: [10, 10], axis: 'y' },
    }, 0.2);

  // ── HOLD (0.5–0.8s) — text + midpoint callback ──
  tl.to(textEl, {
    scale: 1,
    opacity: 1,
    duration: 0.2,
    ease: 'back.out(1.7)',
  }, 0.45)
    .call(onMidpoint, undefined, 0.6)
    .to(textEl, {
      scale: 1.05,
      duration: 0.15,
      yoyo: true,
      repeat: 1,
      ease: 'sine.inOut',
    }, 0.65);

  // ── CRT SHUTDOWN (0.8–1.6s) — vertical compress + white flash line ──
  tl.to(textEl, {
    opacity: 0,
    duration: 0.15,
    ease: 'power1.in',
  }, 0.8)
    // All tiles compress vertically
    .to(tiles, {
      scaleY: 0,
      duration: 0.3,
      ease: 'power2.in',
      stagger: { each: 0.002, from: 'edges', grid: [10, 10], axis: 'y' },
    }, 0.9)
    // Middle rows (indices 40–59) flash white as CRT horizontal line
    .to(tiles.slice(40, 60), {
      backgroundColor: '#FFFFFF',
      scaleY: 1,
      opacity: 1,
      duration: 0.1,
      ease: 'power1.out',
    }, 1.15)
    .to(tiles.slice(40, 60), {
      scaleX: 0,
      opacity: 0,
      duration: 0.2,
      ease: 'power3.in',
    }, 1.25)
    .to(scanlinesEl, { opacity: 0, duration: 0.15 }, 1.1)
    .set(overlayEl, { visibility: 'hidden', pointerEvents: 'none' }, 1.6)
    // Reset tiles to clean state
    .set(tiles, { x: 0, y: 0, rotation: 0, scale: 0, scaleX: 1, scaleY: 1, opacity: 0 }, 1.6)
    .call(onComplete, undefined, 1.6);

  return tl;
}
